import { MAX_PARTICIPANTS } from '../../models/messaging-conversation';
import { not } from 'ember-truth-helpers';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import BaseRoute from './base-route.gjs';
import Component from '@glimmer/component';
import ContactListItem from '../contact-list-item.gjs';
import ContactSearch from '../contact-search.gjs';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import PrimaryButton from 'ember-simplepractice/components/spds/action/button/primary';
import SharedInfinityLoader from 'ember-simplepractice/components/shared/infinity-loader';
import SpdsFeedbackAlertBannerMessage from 'ember-simplepractice/components/spds/feedback/alert-banner/message';
import styles from './contacts.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export default class MessagingRoutesContacts extends Component {
  @service intl;
  @service messaging;
  @service messagingNavigation;

  get routeHandler() {
    return this.args.handler;
  }

  get groupMessagingEnabled() {
    return this.routeHandler.groupMessagingEnabled;
  }

  get search() {
    return this.routeHandler.search;
  }

  get showRecentClients() {
    return !this.showRelatedClients && !this.routeHandler.lastFetchWasSearch;
  }

  get showRelatedClients() {
    return this.routeHandler.hasSelectedClient && !this.routeHandler.lastFetchWasSearch;
  }

  get selectedContacts() {
    return this.routeHandler.selectedContacts;
  }

  get contactList() {
    if (!this.routeHandler.selectableContacts || this.routeHandler.getContacts.isRunning) {
      return [];
    }

    const contacts = this.routeHandler.selectableContacts;
    return this.showRecentClients ? contacts.filter(contact => contact.isRecent) : contacts;
  }

  get reachedMaxParticipants() {
    const max = MAX_PARTICIPANTS - 1; // - 1 because the current user is included
    return this.selectedContacts.length >= max;
  }

  get isCreateButtonDisabled() {
    return this.selectedContacts.length < 1;
  }

  get contactListHeading() {
    if (this.showRelatedClients) {
      return this.intl.t('emc.contacts_page.related_clients');
    }
    if (this.showRecentClients) {
      return this.intl.t('emc.contacts_page.recent_clients');
    }
    return null;
  }

  get emptyStateText() {
    if (this.routeHandler.lastFetchWasSearch) {
      return this.intl.t('emc.contacts_page.no_search_contacts_found');
    }

    if (this.selectedContacts.length) {
      return this.intl.t('emc.contacts_page.no_related_contacts_found');
    }

    return this.intl.t('emc.contacts_page.no_contacts_found');
  }

  leaveContactPage = async () => {
    await this.messagingNavigation.goToPreviousRoute();
  };

  updateSearch = event => {
    this.routeHandler.updateSearch(event.target.value);
  };

  clearSearch = () => {
    this.routeHandler.clearSearch();
  };

  loadMoreContacts = model => {
    this.routeHandler.loadMoreContacts.perform(model);
  };

  selectContact = contact => {
    this.routeHandler.selectContact(contact);
    if (this.groupMessagingEnabled) {
      // Refocus search input for easier continued searching and selection.
      document.getElementById('contactSearchInput')?.focus();
    }
  };

  createConversation = () => {
    this.routeHandler.findOrCreateConversationWithContacts(this.selectedContacts);
  };

  <template>
    <BaseRoute class="{{styles.component}} contacts-page" data-test-contacts-route>
      <:header>
        <GhostButton
          @fitContent={{true}}
          @iconOnly={{true}}
          {{on "click" this.leaveContactPage}}
          class="back-button"
          data-test-contacts-page-back-button
        >
          {{svgJar "messaging-chevron-left"}}
        </GhostButton>
        <span data-test-contacts-page-title>
          {{t "emc.contacts_page.new_message"}}
        </span>
      </:header>
      <:default>
        <ContactSearch
          @clearSearch={{this.clearSearch}}
          @groupMessagingEnabled={{this.groupMessagingEnabled}}
          @onSelect={{this.selectContact}}
          @search={{this.search}}
          @selectedContacts={{this.selectedContacts}}
          @updateSearch={{this.updateSearch}}
        />
        {{#if this.reachedMaxParticipants}}
          <div class="contacts-banner">
            <SpdsFeedbackAlertBannerMessage
              @type="warning"
              @body={{t "emc.contacts_page.max_participants_banner"}}
              @isPermanent={{true}}
              @global={{true}}
              data-test-max-participants-banner
            />
          </div>
        {{/if}}
        {{#if this.routeHandler.getContacts.isRunning}}
          <div
            class="spinner"
            data-test-loading-spinner
            aria-live="polite"
            aria-label={{t "emc.contacts_page.loading"}}
          >
            {{svgJar "messaging-spinner"}}
          </div>
        {{else if this.contactList}}
          {{#if this.contactListHeading}}
            <div class="contact-list-heading" data-test-contact-list-heading>
              {{this.contactListHeading}}
            </div>
          {{/if}}
          <ul
            class="contact-list-body {{if this.reachedMaxParticipants 'disabled'}}"
            data-test-contact-list-body
            aria-label={{t "emc.contacts_page.list"}}
            tabindex="-1"
          >
            {{#each this.contactList as |contact|}}
              <ContactListItem
                @contact={{contact}}
                @groupMessagingEnabled={{this.groupMessagingEnabled}}
                @inert={{this.reachedMaxParticipants}}
                @onSelect={{this.selectContact}}
              />
            {{/each}}
            {{#unless this.showRecentClients}}
              <SharedInfinityLoader
                @model={{this.routeHandler.contacts}}
                @infinityLoad={{this.loadMoreContacts}}
                aria-label={{t "emc.contacts_page.infinity_loader_label"}}
              />
            {{/unless}}
          </ul>
        {{else if (not this.reachedMaxParticipants)}}
          <div class="contacts-banner">
            <SpdsFeedbackAlertBannerMessage
              @type="info"
              @body={{this.emptyStateText}}
              @isPermanent={{true}}
              @global={{true}}
              data-test-no-contacts-found-banner
            />
          </div>
        {{/if}}
      </:default>
      <:footer>
        {{#if this.groupMessagingEnabled}}
          <PrimaryButton
            @disabled={{this.isCreateButtonDisabled}}
            {{on "click" this.createConversation}}
            data-test-create-button
          >
            {{t "emc.contacts_page.create"}}
            {{#if this.selectedContacts.length}}
              ({{this.selectedContacts.length}})
            {{/if}}
          </PrimaryButton>
        {{/if}}
      </:footer>
    </BaseRoute>
  </template>
}
